import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ChevronLeft, Shield, AlertTriangle, UserCheck } from 'lucide-react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 lg:px-8 h-16 flex items-center">
        <div className="max-w-4xl mx-auto w-full flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" className="text-slate-600">
              <ChevronLeft className="w-5 h-5 mr-1" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="py-12 px-4 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl border border-slate-200 p-8 lg:p-12 shadow-sm">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Privacy Policy
                </h1>
                <p className="text-slate-500">POPIA Compliant | Effective Date: February 2025</p>
              </div>
            </div>

            <div className="prose prose-slate max-w-none">
              {/* POPIA Notice */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-8 flex gap-3">
                <Shield className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                <div>
                  <p className="text-emerald-800 font-semibold mb-1">POPIA Compliance</p>
                  <p className="text-emerald-700 text-sm">
                    This Privacy Policy complies with the Protection of Personal Information Act 4 of 2013 (POPIA) of South Africa. 
                    We are committed to protecting your personal information and respecting your privacy rights.
                  </p>
                </div>
              </div>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">1. Introduction and Responsible Party</h2>
                <p className="text-slate-600 mb-4">
                  Career Step ("we", "our", or "us") is the responsible party for the processing of your personal information as defined in POPIA. 
                  This Privacy Policy explains how we collect, use, store, share, and protect your personal information when you use our CV creation and job readiness platform.
                </p>
                <p className="text-slate-600">
                  By using Career Step, you acknowledge that you have read and understood this Privacy Policy and consent to the collection and processing of your personal information as described herein.
                </p>
              </section>

              {/* Information Officer */}
              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">2. Information Officer</h2>
                <p className="text-slate-600 mb-4">
                  In accordance with POPIA, we have appointed an Information Officer responsible for ensuring compliance with data protection requirements:
                </p>
                <div className="bg-slate-50 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <UserCheck className="w-5 h-5 text-slate-600" />
                    <p className="text-slate-700 font-semibold">Information Officer</p>
                  </div>
                  <p className="text-slate-600 mb-1"><strong>Email:</strong> support@careerstep.co.za</p>
                  <p className="text-slate-600 text-sm mt-3">
                    You may contact our Information Officer for any privacy-related queries, access requests, or complaints.
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">3. Personal Information We Collect</h2>
                
                <h3 className="text-lg font-semibold text-slate-800 mb-3">3.1 Information You Provide Directly</h3>
                <p className="text-slate-600 mb-3">When you register and use our Service, we collect:</p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                  <li><strong>Identity Information:</strong> Full name, ID number (if provided for verification);</li>
                  <li><strong>Contact Information:</strong> Email address, phone number;</li>
                  <li><strong>Location Information:</strong> City, province;</li>
                  <li><strong>Professional Information:</strong> Work experience, job titles, employers, dates of employment, job descriptions;</li>
                  <li><strong>Educational Information:</strong> Qualifications, institutions, fields of study, graduation dates;</li>
                  <li><strong>Skills and Certifications:</strong> Professional skills, certificates, licences;</li>
                  <li><strong>References:</strong> Names and contact details of professional references you choose to include;</li>
                  <li><strong>Account Credentials:</strong> Email address and encrypted password.</li>
                </ul>

                <h3 className="text-lg font-semibold text-slate-800 mb-3">3.2 Information Collected Automatically</h3>
                <p className="text-slate-600 mb-3">When you use our Service, we automatically collect:</p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                  <li><strong>Device Information:</strong> Device type, operating system, browser type and version;</li>
                  <li><strong>Usage Data:</strong> Pages visited, features used, time spent on the platform;</li>
                  <li><strong>Log Data:</strong> IP address, access times, referring URLs;</li>
                  <li><strong>Cookies and Similar Technologies:</strong> As described in our <Link to="/cookies" className="text-amber-600 hover:underline">Cookie Policy</Link>.</li>
                </ul>

                <h3 className="text-lg font-semibold text-slate-800 mb-3">3.3 Payment Information</h3>
                <p className="text-slate-600">
                  Payment information (credit card numbers, bank account details) is processed directly by our payment provider, PayFast. 
                  We do not store your full payment card details on our servers. We only receive confirmation of successful payments and basic transaction records.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">4. Purpose of Processing</h2>
                <p className="text-slate-600 mb-3">We process your personal information for the following purposes:</p>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-slate-600 mb-4">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-3 font-semibold text-slate-800">Purpose</th>
                        <th className="text-left p-3 font-semibold text-slate-800">Legal Basis (POPIA)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      <tr>
                        <td className="p-3">Providing CV creation and job readiness services</td>
                        <td className="p-3">Contract performance</td>
                      </tr>
                      <tr>
                        <td className="p-3">Processing subscription payments</td>
                        <td className="p-3">Contract performance</td>
                      </tr>
                      <tr>
                        <td className="p-3">AI-powered content enhancement</td>
                        <td className="p-3">Contract performance, Consent</td>
                      </tr>
                      <tr>
                        <td className="p-3">Account management and authentication</td>
                        <td className="p-3">Contract performance</td>
                      </tr>
                      <tr>
                        <td className="p-3">Communicating service updates and changes</td>
                        <td className="p-3">Legitimate interest, Contract</td>
                      </tr>
                      <tr>
                        <td className="p-3">Improving and developing new features</td>
                        <td className="p-3">Legitimate interest</td>
                      </tr>
                      <tr>
                        <td className="p-3">Security, fraud prevention, and abuse detection</td>
                        <td className="p-3">Legitimate interest, Legal obligation</td>
                      </tr>
                      <tr>
                        <td className="p-3">Compliance with legal obligations</td>
                        <td className="p-3">Legal obligation</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">5. AI Processing and Your Data</h2>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-4">
                  <h3 className="text-lg font-semibold text-blue-800 mb-2">How We Use AI</h3>
                  <p className="text-blue-700 mb-3">
                    Our platform uses artificial intelligence to enhance your CV content. Here's what you need to know:
                  </p>
                  <ul className="list-disc pl-6 text-blue-700 space-y-2">
                    <li><strong>Purpose:</strong> AI processes the text you provide to improve language, structure, and professional presentation.</li>
                    <li><strong>Ethical AI:</strong> Our AI is designed to never invent, fabricate, or exaggerate information. It only enhances what you provide.</li>
                    <li><strong>Secure Processing:</strong> AI processing occurs through encrypted connections with our AI service providers.</li>
                    <li><strong>No Training on Your Data:</strong> Your personal information is not used to train AI models.</li>
                    <li><strong>Human Review:</strong> You always have the opportunity to review and modify AI suggestions before use.</li>
                  </ul>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">6. Data Sharing and Third Parties</h2>
                <p className="text-slate-600 mb-4">
                  <strong>We do not sell your personal information.</strong> We may share your information only in the following circumstances:
                </p>
                
                <h3 className="text-lg font-semibold text-slate-800 mb-2">6.1 Service Providers</h3>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                  <li><strong>PayFast:</strong> Payment processing (South African payment gateway, POPIA compliant);</li>
                  <li><strong>AI Service Providers:</strong> Content enhancement processing (data is transmitted securely and not retained);</li>
                  <li><strong>Cloud Infrastructure:</strong> Secure data storage and hosting;</li>
                  <li><strong>Analytics Providers:</strong> Anonymous usage analytics to improve our Service.</li>
                </ul>

                <h3 className="text-lg font-semibold text-slate-800 mb-2">6.2 Legal Requirements</h3>
                <p className="text-slate-600 mb-4">
                  We may disclose your information when required by South African law, court order, or government request, or to protect our legal rights.
                </p>

                <h3 className="text-lg font-semibold text-slate-800 mb-2">6.3 Business Transfers</h3>
                <p className="text-slate-600">
                  In the event of a merger, acquisition, or sale of assets, your personal information may be transferred. We will notify you of any such change.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">7. Data Security</h2>
                <p className="text-slate-600 mb-3">
                  We implement appropriate technical and organisational measures to protect your personal information:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li><strong>Encryption:</strong> All data transmitted to and from our Service is encrypted using TLS/HTTPS;</li>
                  <li><strong>Password Security:</strong> Passwords are hashed using industry-standard algorithms and never stored in plain text;</li>
                  <li><strong>Access Controls:</strong> Access to personal information is restricted to authorised personnel only;</li>
                  <li><strong>Secure Infrastructure:</strong> Our systems are hosted on secure cloud infrastructure with regular security updates;</li>
                  <li><strong>Monitoring:</strong> We monitor for security threats and unauthorised access attempts.</li>
                </ul>
                <p className="text-slate-600 mt-4">
                  While we take reasonable precautions, no method of transmission over the Internet is 100% secure. 
                  You are responsible for keeping your account credentials confidential.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">8. Data Retention</h2>
                <p className="text-slate-600 mb-3">
                  We retain your personal information for as long as necessary to:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                  <li>Provide you with the Service while your account is active;</li>
                  <li>Comply with legal, accounting, or reporting requirements;</li>
                  <li>Resolve disputes and enforce our agreements.</li>
                </ul>
                <p className="text-slate-600 mb-3">
                  <strong>Retention Periods:</strong>
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li><strong>Active Accounts:</strong> Data retained for the duration of your subscription;</li>
                  <li><strong>Deleted Accounts:</strong> Personal data permanently deleted within 30 days of account deletion request;</li>
                  <li><strong>Financial Records:</strong> Payment records retained for 5 years as required by South African tax law;</li>
                  <li><strong>Anonymised Analytics:</strong> May be retained indefinitely for service improvement.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">9. Your Rights Under POPIA</h2>
                <p className="text-slate-600 mb-4">
                  Under the Protection of Personal Information Act, you have the following rights:
                </p>
                
                <div className="space-y-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-800 mb-1">Right to Access (Section 23)</h4>
                    <p className="text-slate-600 text-sm">You may request confirmation of whether we hold your personal information and request a copy of such information.</p>
                  </div>
                  
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-800 mb-1">Right to Correction (Section 24)</h4>
                    <p className="text-slate-600 text-sm">You may request correction or deletion of personal information that is inaccurate, irrelevant, excessive, out of date, incomplete, or misleading.</p>
                  </div>
                  
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-800 mb-1">Right to Deletion (Section 24)</h4>
                    <p className="text-slate-600 text-sm">You may request deletion of your personal information, subject to legal retention requirements.</p>
                  </div>
                  
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-800 mb-1">Right to Object (Section 11(3))</h4>
                    <p className="text-slate-600 text-sm">You may object to the processing of your personal information on reasonable grounds.</p>
                  </div>
                  
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-800 mb-1">Right to Withdraw Consent</h4>
                    <p className="text-slate-600 text-sm">Where processing is based on consent, you may withdraw your consent at any time.</p>
                  </div>
                  
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-800 mb-1">Right to Lodge a Complaint (Section 74)</h4>
                    <p className="text-slate-600 text-sm">You may lodge a complaint with the Information Regulator if you believe your privacy rights have been violated.</p>
                  </div>
                </div>

                <p className="text-slate-600 mt-4">
                  To exercise any of these rights, please contact us at <strong>support@careerstep.co.za</strong>. 
                  We will respond to your request within 30 days.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">10. Cross-Border Data Transfers</h2>
                <p className="text-slate-600 mb-4">
                  Your personal information may be processed by service providers located outside South Africa (e.g., cloud infrastructure, AI services). 
                  When we transfer data internationally, we ensure that:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>The recipient country has adequate data protection laws; or</li>
                  <li>The recipient is bound by a binding agreement ensuring POPIA-equivalent protection; or</li>
                  <li>You have provided consent for the specific transfer.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">11. Data Breach Notification</h2>
                <p className="text-slate-600 mb-4">
                  In accordance with POPIA, if we become aware of a data breach that may compromise the security of your personal information:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>We will notify the Information Regulator as soon as reasonably possible;</li>
                  <li>We will notify affected individuals if the breach poses a risk of harm;</li>
                  <li>We will provide details of the nature of the breach and steps you can take to protect yourself;</li>
                  <li>We will take immediate steps to mitigate any potential harm;</li>
                  <li>We maintain incident response procedures to handle security incidents effectively.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">12. Children's Privacy</h2>
                <p className="text-slate-600">
                  Our Service is not intended for individuals under the age of 16. We do not knowingly collect personal information from children under 16. 
                  If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately at support@careerstep.co.za. 
                  If we become aware that we have collected personal information from a child under 16 without parental consent, we will take steps to delete that information.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">13. Direct Marketing</h2>
                <p className="text-slate-600 mb-4">
                  We may send you information about our services, updates, and promotional content. In accordance with POPIA:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>We will only send marketing communications if you have opted in or if we have a legitimate interest;</li>
                  <li>You may opt out of marketing communications at any time by clicking "unsubscribe" in our emails or updating your account preferences;</li>
                  <li>Opting out of marketing does not affect service-related communications (e.g., subscription confirmations, security notices).</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">14. Cookies and Tracking Technologies</h2>
                <p className="text-slate-600 mb-4">
                  We use cookies and similar technologies to enhance your experience. These include:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li><strong>Essential Cookies:</strong> Required for the Service to function (authentication, security);</li>
                  <li><strong>Analytics Cookies:</strong> Help us understand how users interact with our Service;</li>
                  <li><strong>Preference Cookies:</strong> Remember your settings and preferences.</li>
                </ul>
                <p className="text-slate-600 mt-3">
                  You can manage cookie preferences through your browser settings. Note that disabling certain cookies may affect functionality.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">15. Changes to This Policy</h2>
                <p className="text-slate-600 mb-4">
                  We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. When we make changes:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>We will update the "Effective Date" at the top of this page;</li>
                  <li>For material changes, we will notify you via email or prominent notice on our platform;</li>
                  <li>We encourage you to review this policy periodically;</li>
                  <li>Your continued use of the Service after changes constitutes acceptance of the updated policy.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">16. Information Regulator</h2>
                <p className="text-slate-600 mb-4">
                  If you are not satisfied with how we handle your personal information or our response to your requests, 
                  you have the right to lodge a complaint with the Information Regulator of South Africa:
                </p>
                <div className="bg-slate-50 rounded-xl p-5">
                  <p className="text-slate-700 font-semibold mb-2">Information Regulator (South Africa)</p>
                  <p className="text-slate-600 mb-1"><strong>Website:</strong> www.justice.gov.za/inforeg</p>
                  <p className="text-slate-600 mb-1"><strong>Email:</strong> enquiries@inforegulator.org.za</p>
                  <p className="text-slate-600"><strong>Tel:</strong> 012 406 4818</p>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-bold text-slate-900 mb-4">17. Contact Us</h2>
                <p className="text-slate-600 mb-4">
                  For questions, concerns, or requests regarding this Privacy Policy or your personal information, please contact us:
                </p>
                <div className="bg-slate-50 rounded-xl p-5">
                  <p className="text-slate-700 font-semibold text-lg mb-3">Career Step</p>
                  <p className="text-slate-600 mb-1"><strong>Information Officer:</strong> support@careerstep.co.za</p>
                  <p className="text-slate-600 mb-1"><strong>General Support:</strong> support@careerstep.co.za</p>
                  <p className="text-slate-600"><strong>Website:</strong> www.careerstep.co.za</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicyPage;
